import React, { createContext, ReactNode } from 'react';
import {
  AbilityBuilder,
  AbilityClass,
  ConditionsMatcher,
  FieldMatcher,
  MongoQueryOperators,
  PureAbility,
} from '@casl/ability';
import useStore from '../components/Hooks/useStore';
import { Public } from '@casl/ability/dist/types/RuleIndex';
import { $in, $eq, Query, createQueryTester as sift } from 'sift';

type RestrictedMongoQuery = Record<
  PropertyKey,
  | number
  | string
  | {
      $eq?: MongoQueryOperators['$eq'];
      $in?: MongoQueryOperators['$in'];
    }
>;

export type AppAbility = Public<
  PureAbility<[Actions, Subjects], RestrictedMongoQuery>
>;

type AbilityContextType = AppAbility | null;

export const AbilityContext = createContext<AbilityContextType>(null);

type AbilityProviderProps = {
  children: ReactNode;
};

export const AbilityProvider: React.FC<AbilityProviderProps> = ({
  children,
}) => {
  const [getters] = useStore();

  const {
    user: { getCurrentUser },
  } = getters;

  const user = getCurrentUser();
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(
    PureAbility as AbilityClass<AppAbility>,
  );

  user.roles.forEach(({ rules }) => {
    rules.forEach(
      ({ action, subject, inverted = false, fields, condition }) => {
        if (!inverted) {
          if (condition && fields) {
            can(action, subject, fields, condition);
          } else if (condition) {
            can(action, subject, condition);
          } else if (fields) {
            can(action, subject, fields);
          } else {
            can(action, subject);
          }
        } else {
          if (condition && fields) {
            cannot(action, subject, fields, condition);
          } else if (condition) {
            cannot(action, subject, condition);
          } else if (fields) {
            cannot(action, subject, fields);
          } else {
            cannot(action, subject);
          }
        }
      },
    );
  });

  const fieldMatcher: FieldMatcher = (fields) => (field) =>
    fields.includes(field);

  const conditionsMatcher: ConditionsMatcher<RestrictedMongoQuery> = (
    conditions,
  ) => {
    return sift(conditions as Query<any>, { operations: { in: $in, eq: $eq } });
  };

  return (
    <AbilityContext.Provider value={build({ fieldMatcher, conditionsMatcher })}>
      {children}
    </AbilityContext.Provider>
  );
};
